import React, { useState, useRef, useCallback } from 'react';
import { Mic, Square, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VoiceRecorder = ({ onTranscription, disabled = false }) => {
  const { t } = useLanguage();
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        setIsProcessing(true);
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        
        try {
          const formData = new FormData();
          formData.append('file', audioBlob, 'recording.webm');
          formData.append('language', 'de');

          const response = await axios.post(`${API}/speech/transcribe`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            withCredentials: true
          });

          if (response.data.text) {
            onTranscription(response.data.text);
          }
        } catch (error) {
          // error handled silently
        } finally {
          setIsProcessing(false);
        }

        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      // error handled silently
    }
  }, [onTranscription]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  const handleClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <Button
      onClick={handleClick}
      disabled={disabled || isProcessing}
      variant={isRecording ? 'destructive' : 'default'}
      size="lg"
      className={`relative rounded-full w-14 h-14 p-0 ${
        isRecording ? 'voice-btn-recording animate-pulse' : ''
      }`}
      data-testid="voice-recorder-btn"
    >
      {isProcessing ? (
        <Loader2 className="h-6 w-6 animate-spin" />
      ) : isRecording ? (
        <Square className="h-6 w-6" />
      ) : (
        <Mic className="h-6 w-6" />
      )}
    </Button>
  );
};

export const AudioVisualizer = ({ isActive }) => {
  if (!isActive) return null;

  return (
    <div className="audio-visualizer flex items-center justify-center gap-1 h-8" data-testid="audio-visualizer">
      {[...Array(5)].map((_, i) => (
        <div
          key={`audio-bar-${i}`}
          className="audio-bar w-1 bg-primary rounded-full"
          style={{
            animationDelay: `${i * 0.1}s`,
            height: `${Math.random() * 16 + 8}px`
          }}
        />
      ))}
    </div>
  );
};
